import { z } from 'zod'
import { taxBpsSchema } from './money.ts'
import { unitCodeSchema } from './units.ts'

/**
 * The canonical product master.
 *
 * One shared list, owned by admin, that every listing and every order line points
 * at. If sellers typed free text you would get "OPC 43", "opc-43 cement" and
 * "Cement 43grade", and comparing two shops' prices would stop being possible —
 * see docs/01-product-design.md §3.
 */

export const CATALOG_STATUSES = ['active', 'archived'] as const
export type CatalogStatus = (typeof CATALOG_STATUSES)[number]
export const catalogStatusSchema = z.enum(CATALOG_STATUSES)

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

/**
 * Two levels, deliberately. A shop owner navigating on a phone can hold "Cement &
 * Aggregates → Cement" in their head; a five-deep tree is a filing system nobody
 * maintains.
 */
export const MAX_CATEGORY_DEPTH = 2

export const categorySchema = z.object({
  id: z.string(),
  parentId: z.string().nullable(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  sortOrder: z.number().int(),
  status: catalogStatusSchema,
  createdAt: z.number().int(),
  version: z.number().int(),
})
export type Category = z.infer<typeof categorySchema>

/** A category plus what a list screen needs: its children and how full it is. */
export const categoryNodeSchema = categorySchema.extend({
  productCount: z.number().int(),
  children: z.array(categorySchema.extend({ productCount: z.number().int() })),
})
export type CategoryNode = z.infer<typeof categoryNodeSchema>

export const createCategorySchema = z.object({
  parentId: z.string().max(64).nullish(),
  name: z.string().trim().min(2, 'Enter a category name.').max(80),
  description: z.string().trim().max(300).nullish(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
})
export type CreateCategoryInput = z.infer<typeof createCategorySchema>

export const updateCategorySchema = createCategorySchema.partial().extend({
  status: catalogStatusSchema.optional(),
})
export type UpdateCategoryInput = z.infer<typeof updateCategorySchema>

// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------

export const productSchema = z.object({
  id: z.string(),
  categoryId: z.string().nullable(),
  categoryName: z.string().nullable(),
  name: z.string(),
  brand: z.string().nullable(),
  model: z.string().nullable(),
  description: z.string().nullable(),
  /** The unit it is sold and priced in — see decision D3. */
  unitCode: z.string(),
  /** Content of one selling unit, e.g. 50 kg in one bag. */
  packQty: z.number().int().nullable(),
  packUnitCode: z.string().nullable(),
  taxBps: z.number().int(),
  imageAssetId: z.string().nullable(),
  status: catalogStatusSchema,
  createdAt: z.number().int(),
  version: z.number().int(),
})
export type Product = z.infer<typeof productSchema>

/** The embedded form used wherever a listing or order line names its product. */
export const productSummarySchema = productSchema.pick({
  id: true,
  name: true,
  brand: true,
  unitCode: true,
  packQty: true,
  packUnitCode: true,
  imageAssetId: true,
  categoryName: true,
})
export type ProductSummary = z.infer<typeof productSummarySchema>

/**
 * `packQty` and `packUnitCode` travel together: "1 bag" is meaningless and
 * "1 bag = 50" is worse, because it silently invites the reader to guess the unit.
 *
 * Applied with `.refine()` at each call site rather than through a helper: a helper
 * typed `z.ZodType<T>` widens the schema's *input* type to `unknown`, which
 * silently breaks `zodResolver` on the client.
 */
const packsMatch = (v: { packQty?: number | null; packUnitCode?: string | null }) =>
  (v.packQty == null) === !v.packUnitCode

const PACK_MESSAGE = {
  message: 'Give both the pack size and its unit, or neither.',
  path: ['packQty'],
}

const productFields = z.object({
  categoryId: z.string().max(64).nullish(),
  name: z.string().trim().min(2, 'Enter the item name.').max(160),
  brand: z.string().trim().max(80).nullish(),
  model: z.string().trim().max(80).nullish(),
  description: z.string().trim().max(2000).nullish(),
  unitCode: unitCodeSchema,
  packQty: z.number().int().min(1).max(1_000_000).nullish(),
  packUnitCode: z.union([z.literal(''), unitCodeSchema]).nullish(),
  // Optional rather than `.default()`: a default makes the schema's input and
  // output types differ, and react-hook-form types a form by one of them. The
  // server falls back to DEFAULT_VAT_BPS, which is the right place for it anyway.
  taxBps: taxBpsSchema.optional(),
  imageAssetId: z.string().max(64).nullish(),
})

export const createProductSchema = productFields.refine(packsMatch, PACK_MESSAGE)
export type CreateProductInput = z.infer<typeof createProductSchema>

export const updateProductSchema = productFields
  .partial()
  .extend({ status: catalogStatusSchema.optional() })
  .refine(packsMatch, PACK_MESSAGE)
export type UpdateProductInput = z.infer<typeof updateProductSchema>

/**
 * Merging a duplicate keeps the loser's name as an alias, so the next person who
 * searches for it lands on the survivor. Search quality improves every time an
 * admin cleans up, which is the point.
 */
export const mergeProductSchema = z.object({
  /** The row that survives. */
  targetId: z.string().min(1),
  keepAlias: z.boolean().default(true),
})
export type MergeProductInput = z.infer<typeof mergeProductSchema>

export const productAliasSchema = z.object({
  id: z.string(),
  productId: z.string(),
  alias: z.string(),
  source: z.enum(['manual', 'merge', 'proposal']),
  createdAt: z.number().int(),
})
export type ProductAlias = z.infer<typeof productAliasSchema>

/** A search hit. `matchedAlias` is what to show when the query hit an old name. */
export const productMatchSchema = productSummarySchema.extend({
  matchedAlias: z.string().nullable(),
})
export type ProductMatch = z.infer<typeof productMatchSchema>

// ---------------------------------------------------------------------------
// Proposals — how a seller adds something the master does not have yet
// ---------------------------------------------------------------------------

export const PROPOSAL_STATUSES = ['pending', 'approved', 'rejected', 'merged'] as const
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number]
export const proposalStatusSchema = z.enum(PROPOSAL_STATUSES)

export const PROPOSAL_STATUS_LABELS: Record<ProposalStatus, string> = {
  pending: 'Waiting for review',
  approved: 'Added to the catalog',
  rejected: 'Not added',
  merged: 'Matched to an existing item',
}

export const productProposalSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  /** Admin-facing only; a proposal never reaches a buyer. */
  orgName: z.string().nullable(),
  proposedByName: z.string().nullable(),
  name: z.string(),
  brand: z.string().nullable(),
  categoryId: z.string().nullable(),
  categoryName: z.string().nullable(),
  unitCode: z.string(),
  packQty: z.number().int().nullable(),
  packUnitCode: z.string().nullable(),
  note: z.string().nullable(),
  imageAssetId: z.string().nullable(),
  status: proposalStatusSchema,
  reviewNote: z.string().nullable(),
  resultProductId: z.string().nullable(),
  reviewedAt: z.number().int().nullable(),
  createdAt: z.number().int(),
  version: z.number().int(),
})
export type ProductProposal = z.infer<typeof productProposalSchema>

export const createProposalSchema = z
  .object({
    name: z.string().trim().min(2, 'Enter the item name.').max(160),
    brand: z.string().trim().max(80).nullish(),
    categoryId: z.string().max(64).nullish(),
    unitCode: unitCodeSchema,
    packQty: z.number().int().min(1).max(1_000_000).nullish(),
    packUnitCode: z.union([z.literal(''), unitCodeSchema]).nullish(),
    note: z.string().trim().max(500).nullish(),
    imageAssetId: z.string().max(64).nullish(),
  })
  .refine(packsMatch, PACK_MESSAGE)
export type CreateProposalInput = z.infer<typeof createProposalSchema>

/**
 * The moderation decision. `merge` is the interesting one: it points the seller at
 * an existing product and records their wording as an alias.
 */
export const reviewProposalSchema = z.discriminatedUnion('decision', [
  z.object({
    decision: z.literal('approve'),
    categoryId: z.string().max(64).nullish(),
    /** Admin may tidy the name before it becomes canonical. */
    name: z.string().trim().min(2).max(160).optional(),
    taxBps: taxBpsSchema.optional(),
    note: z.string().trim().max(500).nullish(),
  }),
  z.object({
    decision: z.literal('merge'),
    productId: z.string().min(1, 'Pick the item this matches.'),
    note: z.string().trim().max(500).nullish(),
  }),
  z.object({
    decision: z.literal('reject'),
    note: z.string().trim().min(2, 'Say why, so the shop can fix it.').max(500),
  }),
])
export type ReviewProposalInput = z.infer<typeof reviewProposalSchema>
